import { AccountsPayableCreate } from '../models/AccountsPayable';
import {
  buildInstallmentDueDates,
  formatReceivableDateForApi,
  splitAmountIntoInstallments
} from './accounts-receivable.utils';

export interface CostCenterAllocationInput {
  costCenterId: number;
  percent: number;
}

export interface AccountsPayableBatchBuildOptions {
  base: AccountsPayableCreate;
  firstDueDate: string;
  monthCount: number;
  intervalDays: number;
  /** Valor mensal fixo (rateio). */
  monthlyNetAmount: number;
  /** Valor total do período (sem rateio — dividido entre os meses). */
  periodNetAmount: number;
  allocations: CostCenterAllocationInput[];
  useRateio: boolean;
  singleCostCenterId?: number;
  /** Parcelado: mesmo valor líquido em cada parcela (não divide o total). */
  fixedAmountPerInstallment?: boolean;
}

export interface AccountsPayableBatchPreviewRow {
  lineNumber: number;
  monthIndex: number;
  monthTotal: number;
  installmentNumber: number;
  installmentTotal: number;
  dueDate: string;
  costCenterId: number;
  percent: number;
  amount: number;
  documentNumber: string;
  description: string;
}

/** Divide valor total conforme percentuais (soma deve ser 100). */
export function splitAmountByPercentages(totalAmount: number, percents: number[]): number[] {
  const total = Math.max(0, Number(totalAmount) || 0);
  const weights = percents.map(p => Math.max(0, Number(p) || 0));
  const weightSum = weights.reduce((s, w) => s + w, 0);
  if (weightSum <= 0) {
    return weights.map(() => 0);
  }

  const totalCents = Math.round(total * 100);
  const rawCents = weights.map(w => (totalCents * w) / weightSum);
  const floors = rawCents.map(c => Math.floor(c));
  let remainder = totalCents - floors.reduce((s, c) => s + c, 0);

  const order = rawCents
    .map((c, i) => ({ i, frac: c - floors[i] }))
    .sort((a, b) => b.frac - a.frac);

  const cents = [...floors];
  for (let k = 0; k < order.length && remainder > 0; k++) {
    cents[order[k].i]++;
    remainder--;
  }

  return cents.map(c => c / 100);
}

export function roundPercentTotal(allocations: CostCenterAllocationInput[]): number {
  return allocations.reduce((s, a) => s + (Number(a.percent) || 0), 0);
}

function formatBrl(amount: number): string {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(
    Math.round((Number(amount) || 0) * 100) / 100
  );
}

/** Descrição da linha no lote com divisão: deixa claro que o % é sobre o total do mês. */
export function buildDivisaoLineDescription(
  baseDescription: string | undefined,
  monthIndex: number,
  monthTotal: number,
  months: number,
  percent: number,
  lineAmount: number
): string {
  const pctLabel = `${Number(percent).toFixed(2).replace(/\.?0+$/, '')}%`;
  const totalLabel = formatBrl(monthTotal);
  const lineLabel = formatBrl(lineAmount);
  const divisaoPart = `${pctLabel} de ${totalLabel} (total do mês) — valor da linha ${lineLabel} — Mês ${monthIndex}/${months}`;
  const trimmed = (baseDescription || '').trim();
  return trimmed ? `${trimmed} — ${divisaoPart}` : divisaoPart;
}

export function buildAccountsPayableBatchPreview(
  options: AccountsPayableBatchBuildOptions
): AccountsPayableBatchPreviewRow[] {
  const rows: AccountsPayableBatchPreviewRow[] = [];
  const items = buildAccountsPayableBatchPayload(options);
  items.forEach((item, index) => {
    const monthMatch = item.documentNumber.match(/-(\d{2})-CC/);
    const monthIndex = monthMatch ? Number(monthMatch[1]) : (item.installmentNumber ?? index + 1);
    rows.push({
      lineNumber: index + 1,
      monthIndex,
      monthTotal: options.monthCount,
      installmentNumber: item.installmentNumber ?? 1,
      installmentTotal: item.totalInstallments ?? 1,
      dueDate: String(item.dueDate),
      costCenterId: item.costCenterId ?? 0,
      percent: options.allocations.find(a => a.costCenterId === item.costCenterId)?.percent ?? 0,
      amount: item.amountToPay,
      documentNumber: item.documentNumber,
      description: item.description || ''
    });
  });
  return rows;
}

/** Monta payload POST /accountspayable/batch. */
export function buildAccountsPayableBatchPayload(
  options: AccountsPayableBatchBuildOptions
): AccountsPayableCreate[] {
  const {
    base,
    firstDueDate,
    monthCount,
    intervalDays,
    monthlyNetAmount,
    periodNetAmount,
    allocations,
    useRateio,
    singleCostCenterId,
    fixedAmountPerInstallment = false
  } = options;

  const months = Math.max(1, Math.floor(monthCount));
  const dueDates = buildInstallmentDueDates(firstDueDate, months, Math.max(1, intervalDays));
  const baseDoc = (base.documentNumber || 'AP').trim().replace(/\s+/g, '-');
  const issueDate = base.issueDate || formatReceivableDateForApi(new Date());
  const items: AccountsPayableCreate[] = [];
  let seq = 0;

  const monthAmounts =
    useRateio || fixedAmountPerInstallment
      ? dueDates.map(() => monthlyNetAmount)
      : splitAmountIntoInstallments(periodNetAmount, months);

  const applyChargesOnEveryInstallment = fixedAmountPerInstallment;

  for (let m = 0; m < months; m++) {
    const dueDate = dueDates[m];
    const monthNet = monthAmounts[m] ?? monthlyNetAmount;

    if (useRateio && allocations.length > 0) {
      const percents = allocations.map(a => a.percent);
      const split = splitAmountByPercentages(monthNet, percents);
      const ccTotal = allocations.length;

      allocations.forEach((alloc, aIndex) => {
        seq++;
        const n = aIndex + 1;
        const amount = split[aIndex] ?? 0;
        items.push({
          ...base,
          documentNumber: `${baseDoc}-${String(m + 1).padStart(2, '0')}-CC${alloc.costCenterId}`,
          issueDate,
          dueDate,
          originalAmount: amount,
          amountToPay: amount,
          discountAmount: m === 0 && aIndex === 0 ? base.discountAmount : 0,
          interestAmount: m === 0 && aIndex === 0 ? base.interestAmount : 0,
          fineAmount: m === 0 && aIndex === 0 ? base.fineAmount : 0,
          costCenterId: alloc.costCenterId,
          installmentNumber: n,
          totalInstallments: ccTotal,
          description: buildDivisaoLineDescription(
            base.description,
            m + 1,
            monthNet,
            months,
            alloc.percent,
            amount
          )
        });
      });
    } else {
      seq++;
      const ccId = singleCostCenterId ?? base.costCenterId;
      const chargeOnThisLine = applyChargesOnEveryInstallment || m === 0;
      items.push({
        ...base,
        documentNumber: months > 1 ? `${baseDoc}-${String(seq).padStart(3, '0')}` : baseDoc,
        issueDate,
        dueDate,
        originalAmount: base.originalAmount ?? monthNet,
        amountToPay: monthNet,
        discountAmount: chargeOnThisLine ? base.discountAmount : 0,
        interestAmount: chargeOnThisLine ? base.interestAmount : 0,
        fineAmount: chargeOnThisLine ? base.fineAmount : 0,
        costCenterId: ccId,
        installmentNumber: m + 1,
        totalInstallments: months,
        description: base.description
          ? `${base.description} — Parcela ${m + 1}/${months}`
          : `Parcela ${m + 1}/${months}`
      });
    }
  }

  return items;
}
